import React, { useEffect, useMemo, useState } from "react";
import api, { fmtInt, fmtPct } from "@/api";
import { useI18n } from "@/i18n";
import { PageHeader, Tile, Loading } from "@/components/Bits";

export default function Competitors() {
  const { t } = useI18n();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/competitors").then((r) => setD(r.data));
  }, []);

  const { topSuppliers, topSubs, matrix } = useMemo(() => {
    if (!d) return {};
    const topSuppliers = d.suppliers.slice(0, 12);
    const topSubs = d.sub_systems.slice(0, 16).map((s) => s.sub_system);
    const supSet = new Set(topSuppliers.map((s) => s.supplier));
    const matrix = {};
    for (const m of d.matrix) {
      if (supSet.has(m.supplier) && topSubs.includes(m.sub_system)) {
        matrix[`${m.supplier}|${m.sub_system}`] = m.qty;
      }
    }
    return { topSuppliers, topSubs, matrix };
  }, [d]);

  if (!d) return <Loading />;

  const maxCell = Math.max(...Object.values(matrix), 1);
  const cellBg = (v) => {
    if (!v) return "transparent";
    const a = Math.min(1, v / maxCell);
    return `rgba(237, 32, 39, ${0.08 + a * 0.7})`;
  };

  return (
    <div data-testid="competitors-page">
      <PageHeader title={t("competitors.title")} subtitle={t("competitors.subtitle")} />
      <div className="p-4 md:p-8 space-y-4 md:space-y-6">
        <Tile testId="comp-share">
          <div className="label-tech mb-4">{t("competitors.share")}</div>
          <div className="space-y-1.5">
            {d.sub_systems.map((s) => (
              <div key={s.sub_system} className="grid grid-cols-[180px_1fr_80px_80px] gap-3 items-center text-sm">
                <div className="text-zinc-300">{s.sub_system}</div>
                <div className="h-5 bg-white/5 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-wabtec-red" style={{ width: `${s.wabtec_pct}%` }} />
                </div>
                <div className="num text-right text-wabtec-red text-xs">{fmtPct(s.wabtec_pct)}</div>
                <div className="num text-right text-zinc-500 text-xs">{fmtInt(s.total)}</div>
              </div>
            ))}
          </div>
        </Tile>

        <Tile testId="comp-matrix">
          <div className="label-tech mb-4">{t("competitors.matrix")}</div>
          <div className="overflow-auto">
            <table className="tech text-xs">
              <thead>
                <tr>
                  <th>Supplier ↓ × Sub-system →</th>
                  {topSubs.map((s) => (
                    <th key={s} className="text-right" style={{ minWidth: 60 }}>{s.substring(0, 12)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSuppliers.map((sup) => (
                  <tr key={sup.supplier}>
                    <td className="font-medium">{sup.supplier}</td>
                    {topSubs.map((s) => {
                      const v = matrix[`${sup.supplier}|${s}`];
                      return (
                        <td key={s} className="num text-right" style={{ background: cellBg(v) }}>
                          {v ? fmtInt(v) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tile>
      </div>
    </div>
  );
}
